/** Scroll thresholds for collapsing/expanding insight + timeframe rows. */
// Hide only after a clear scroll past the top; show again near the top.
// Direction threshold is larger than the footer's 4px so small jitter / bounce
// doesn't toggle the 450ms height+opacity+translate animation mid-flight.
export const DRAWER_FILTERS_HIDE_SCROLL_TOP = 24;
export const DRAWER_FILTERS_SHOW_SCROLL_TOP = 8;
export const DRAWER_FILTERS_SCROLL_DIRECTION_THRESHOLD = 16;
/** Hold hide after a show for ~one animation cycle (results-toolbar 450ms ease). */
export const DRAWER_FILTERS_TOGGLE_COOLDOWN_MS = 480;

/**
 * Per-flick upward velocity gate (px/ms).
 *
 * Measured over {@link DRAWER_FILTERS_VELOCITY_SAMPLE_WINDOW_MS}:
 *   velocity = max(0, (oldestScrollTop - newestScrollTop) / Δt_ms)
 *
 * Raised from 0.9 → **1.5 px/ms** after #38 feedback (single slightly-fast
 * reverse scrolls were reopening filters). A qualifying flick must clear this
 * bar; one flick alone never reveals — see {@link DRAWER_FILTERS_FLICKS_TO_REVEAL}.
 *
 * - Medium sustained reverse: ~0.5–1.2 px/ms → no flick counted
 * - Hard flick spike:         ≳1.5 px/ms → one distinct flick
 * - At scrollTop ~0: always show (flick tracker ignored)
 */
export const DRAWER_FILTERS_REVEAL_VELOCITY_PX_PER_MS = 1.5;

/**
 * Velocity must drop below this (px/ms) before another flick can be counted.
 * Prevents one sustained high-speed burst from registering as multiple flicks.
 */
export const DRAWER_FILTERS_FLICK_REARM_VELOCITY_PX_PER_MS = 0.6;

/**
 * Distinct upward flicks required mid-list before filters reappear.
 * A single fast scroll must not reveal; repeated intentional flicks do.
 */
export const DRAWER_FILTERS_FLICKS_TO_REVEAL = 2;

/** Flicks older than this window (ms) expire and no longer count. */
export const DRAWER_FILTERS_FLICK_WINDOW_MS = 1200;

/** Lookback window for upward-velocity samples (ms). Short enough to catch a flick. */
export const DRAWER_FILTERS_VELOCITY_SAMPLE_WINDOW_MS = 80;

/** Hard "at bottom" band (px) for footer pin + filter freeze. */
export const DRAWER_SCROLL_BOTTOM_THRESHOLD_PX = 8;

/**
 * Keep the footer visible (and freeze filters) once within this many px of the
 * bottom so the ±tab-bar height change doesn't fire at the exact end stop.
 */
export const DRAWER_SCROLL_NEAR_BOTTOM_PX = 100;

export type DrawerFiltersScrollDecision =
  | { action: 'hide' }
  | { action: 'show' }
  | { action: 'none' };

export type DrawerFiltersScrollSample = {
  scrollTop: number;
  timeMs: number;
};

/** Mutable tracker for distinct upward flicks toward the top. */
export type DrawerFiltersFlickTracker = {
  /** Timestamps (ms) of qualifying flicks still inside the window. */
  flickTimesMs: number[];
  /**
   * When true, the current high-velocity burst already counted as a flick;
   * wait for velocity to drop below the rearm gate before counting another.
   */
  inFlickBurst: boolean;
};

export function createDrawerFiltersFlickTracker(): DrawerFiltersFlickTracker {
  return { flickTimesMs: [], inFlickBurst: false };
}

/** Clamp scrollTop into [0, maxScroll] so rubber-band values don't leak. */
export function clampScrollTop(scrollTop: number, scrollHeight: number, clientHeight: number): number {
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  return Math.min(Math.max(0, scrollTop), maxScroll);
}

/**
 * True when the scroller has real range and is within `thresholdPx` of the end.
 * Non-scrollable panels (maxScroll === 0) are never "at bottom".
 */
export function isScrollNearBottom(args: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  thresholdPx?: number;
}): boolean {
  const maxScroll = Math.max(0, args.scrollHeight - args.clientHeight);
  if (maxScroll <= 0) return false;
  const top = clampScrollTop(args.scrollTop, args.scrollHeight, args.clientHeight);
  const threshold = args.thresholdPx ?? DRAWER_SCROLL_BOTTOM_THRESHOLD_PX;
  return top >= maxScroll - threshold;
}

/**
 * End infinite scroll when we've loaded everything OR the last page was short
 * (including empty). Mirrors CompareAdvancedView's short-page gate so filtered
 * modes don't keep firing ionInfinite against a stale casino-wide totalCount.
 */
export function shouldEndInfiniteScroll(args: {
  loadedCount: number;
  totalCount: number;
  pageLength: number;
  pageSize: number;
}): boolean {
  if (args.pageLength < args.pageSize) return true;
  if (args.totalCount > 0 && args.loadedCount >= args.totalCount) return true;
  return false;
}

/**
 * Upward scroll speed in px/ms from recent samples (scrollTop decreasing).
 * Returns 0 when there aren't two samples spanning a positive Δt.
 */
export function computeUpwardScrollVelocityPxPerMs(
  samples: readonly DrawerFiltersScrollSample[],
  nowMs: number,
  windowMs: number = DRAWER_FILTERS_VELOCITY_SAMPLE_WINDOW_MS
): number {
  if (samples.length < 2) return 0;
  const windowStart = nowMs - windowMs;
  let oldestIdx = -1;
  for (let i = 0; i < samples.length; i += 1) {
    if (samples[i].timeMs >= windowStart) {
      oldestIdx = i;
      break;
    }
  }
  if (oldestIdx < 0) return 0;
  // Prefer a sample near the window start; if the first in-window sample is
  // the newest, fall back to the prior sample so Δt is non-zero.
  if (oldestIdx === samples.length - 1) {
    oldestIdx = samples.length - 2;
  }
  const oldest = samples[oldestIdx];
  const newest = samples[samples.length - 1];
  const dt = newest.timeMs - oldest.timeMs;
  if (dt <= 0) return 0;
  const upwardDelta = oldest.scrollTop - newest.scrollTop;
  if (upwardDelta <= 0) return 0;
  return upwardDelta / dt;
}

/** Drop samples older than the velocity window (keeps at least the latest). */
export function pruneScrollSamples(
  samples: DrawerFiltersScrollSample[],
  nowMs: number,
  windowMs: number = DRAWER_FILTERS_VELOCITY_SAMPLE_WINDOW_MS
): void {
  const windowStart = nowMs - windowMs;
  while (samples.length > 1 && samples[0].timeMs < windowStart) {
    samples.shift();
  }
}

/**
 * Update the flick tracker from the latest scroll sample.
 *
 * - Downward scroll (delta ≥ direction threshold) → full reset
 * - Flicks older than {@link DRAWER_FILTERS_FLICK_WINDOW_MS} expire
 * - Crossing ≥ reveal velocity while not in a burst → count one flick
 * - Dropping below rearm velocity → allow the next distinct flick
 *
 * Returns the number of flicks still inside the window after this update.
 */
export function updateDrawerFiltersFlickTracker(args: {
  tracker: DrawerFiltersFlickTracker;
  upwardVelocityPxPerMs: number;
  /** scrollTop − previousScrollTop (positive = down the list). */
  scrollDelta: number;
  nowMs: number;
}): number {
  const { tracker, upwardVelocityPxPerMs, scrollDelta, nowMs } = args;

  // Any clear downward movement cancels the multi-flick streak.
  if (scrollDelta >= DRAWER_FILTERS_SCROLL_DIRECTION_THRESHOLD) {
    tracker.flickTimesMs = [];
    tracker.inFlickBurst = false;
    return 0;
  }

  const windowStart = nowMs - DRAWER_FILTERS_FLICK_WINDOW_MS;
  tracker.flickTimesMs = tracker.flickTimesMs.filter((t) => t >= windowStart);

  if (upwardVelocityPxPerMs >= DRAWER_FILTERS_REVEAL_VELOCITY_PX_PER_MS) {
    if (!tracker.inFlickBurst) {
      tracker.flickTimesMs.push(nowMs);
      tracker.inFlickBurst = true;
    }
  } else if (upwardVelocityPxPerMs <= DRAWER_FILTERS_FLICK_REARM_VELOCITY_PX_PER_MS) {
    tracker.inFlickBurst = false;
  }

  return tracker.flickTimesMs.length;
}

/**
 * Pure decision for insight/timeframe visibility from list scroll.
 *
 * Show rules when hidden:
 * - scrollTop ≤ SHOW threshold (top of list) → always show
 * - repeatedFlicksReady (N distinct fast upward flicks in the window) → show
 * - single flick / medium sustained reverse → stay hidden
 *
 * At the bottom of the list, freeze hide/show so rubber-band reverse deltas
 * and flick spikes cannot expand the toolbar and churn content height.
 *
 * Hide rules when visible: unchanged (scroll down past hide threshold).
 * Programmatic pin→row suppress still force-shows.
 */
export function decideDrawerFiltersVisibility(args: {
  scrollTop: number;
  previousScrollTop: number;
  filtersVisible: boolean;
  /** When true, footer hide is suppressed; filters stay/show visible. */
  suppressProgrammaticScroll?: boolean;
  /**
   * True when the flick tracker has reached {@link DRAWER_FILTERS_FLICKS_TO_REVEAL}
   * distinct upward flicks inside the flick window. Single flicks stay false.
   */
  repeatedFlicksReady?: boolean;
  /**
   * True when the scroller is at/near the bottom. Bounce reverse must not
   * toggle filters (that expands the toolbar and stutters the end stop).
   */
  atBottom?: boolean;
}): DrawerFiltersScrollDecision {
  const scrollTop = Math.max(0, args.scrollTop);
  const previous = Math.max(0, args.previousScrollTop);
  const delta = scrollTop - previous;

  if (args.suppressProgrammaticScroll) {
    return args.filtersVisible ? { action: 'none' } : { action: 'show' };
  }

  // At/near top: always show if hidden (covers remount at scrollTop 0 with no delta).
  if (scrollTop <= DRAWER_FILTERS_SHOW_SCROLL_TOP) {
    return args.filtersVisible ? { action: 'none' } : { action: 'show' };
  }

  // Bottom bounce / overscroll reverse — freeze filters (keep existing rules
  // for mid-list hide/show and top/flick reveal elsewhere).
  if (args.atBottom) {
    return { action: 'none' };
  }

  // Mid-list reveal only after repeated distinct fast flicks (not one scroll).
  if (!args.filtersVisible && args.repeatedFlicksReady) {
    return { action: 'show' };
  }

  if (Math.abs(delta) < DRAWER_FILTERS_SCROLL_DIRECTION_THRESHOLD) {
    return { action: 'none' };
  }

  if (delta > 0 && scrollTop > DRAWER_FILTERS_HIDE_SCROLL_TOP && args.filtersVisible) {
    return { action: 'hide' };
  }

  // Slow / medium reverse, or a single flick — keep hidden until top or N flicks.
  return { action: 'none' };
}

/** True when a cached scroll element is still usable for at-bottom checks. */
export function isLiveScrollElement(el: HTMLElement | null | undefined): el is HTMLElement {
  return !!el && el.isConnected;
}
